import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageContainer from '../../components/layout/PageContainer';
import PageField from '../../components/layout/PageField';
import Photo from '../../components/Photo/Photo';
import { services } from '../../content/site';
import { photos } from '../../content/photos';

type MarkColors = {
  blue: string;
  red: string;
  muted: string;
  surface: string;
};

const svgProps = {
  width: '100%',
  height: '180',
  viewBox: '0 0 520 200',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  preserveAspectRatio: 'xMidYMid meet',
  'aria-hidden': true as const,
};

/** Original marks for the four services. The heading already names each one. */
const ServiceArt = ({ index, colors }: { index: number; colors: MarkColors }) => {
  const { blue, red, muted, surface } = colors;

  if (index === 0) {
    return (
      <svg {...svgProps}>
        <circle cx="420" cy="36" r="70" fill={blue} opacity="0.08" />
        <rect x="78" y="28" width="364" height="148" rx="14" fill={surface} stroke={blue} strokeWidth="2" />
        <path d="M78 58h364" stroke={blue} strokeWidth="2" />
        <circle cx="100" cy="43" r="4" fill={red} />
        <circle cx="116" cy="43" r="4" fill={muted} />
        <circle cx="132" cy="43" r="4" fill={muted} />
        <rect x="96" y="76" width="78" height="80" rx="8" fill={blue} opacity="0.12" />
        <path d="M110 96h50M110 112h36M110 128h44" stroke={blue} strokeWidth="2" strokeLinecap="round" />
        <rect x="190" y="76" width="140" height="80" rx="8" stroke={blue} strokeWidth="2" />
        <rect x="206" y="90" width="52" height="8" rx="2" fill={red} />
        <path d="M206 114h96M206 130h72" stroke={muted} strokeWidth="2" strokeLinecap="round" />
        <rect x="346" y="76" width="78" height="36" rx="8" stroke={blue} strokeWidth="2" />
        <rect x="346" y="120" width="78" height="36" rx="8" stroke={blue} strokeWidth="2" />
      </svg>
    );
  }

  if (index === 1) {
    return (
      <svg {...svgProps}>
        <circle cx="96" cy="160" r="64" fill={blue} opacity="0.08" />
        <rect x="46" y="40" width="168" height="120" rx="12" stroke={muted} strokeWidth="2" />
        <path d="M66 72h88M66 92h64M66 112h76" stroke={muted} strokeWidth="2" strokeLinecap="round" />
        <path d="M236 100h40" stroke={red} strokeWidth="2" strokeLinecap="round" />
        <path d="M266 88l14 12-14 12" stroke={red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="306" y="40" width="168" height="120" rx="12" fill={surface} stroke={blue} strokeWidth="2" />
        <path d="M306 52a12 12 0 0 1 12-12h144a12 12 0 0 1 12 12v16H306V52z" fill={blue} />
        <path d="M326 96h88M326 116h64" stroke={blue} strokeWidth="2" strokeLinecap="round" />
        <rect x="430" y="92" width="24" height="24" rx="6" fill={red} />
      </svg>
    );
  }

  if (index === 2) {
    return (
      <svg {...svgProps}>
        <circle cx="260" cy="100" r="78" fill={blue} opacity="0.08" />
        <path d="M128 100h36M250 100h36M372 100h36" stroke={muted} strokeWidth="2" strokeLinecap="round" />
        <rect x="48" y="68" width="80" height="64" rx="12" fill={surface} stroke={blue} strokeWidth="2" />
        <path d="M66 92h44M66 108h28" stroke={muted} strokeWidth="2" strokeLinecap="round" />
        <rect x="164" y="60" width="86" height="80" rx="14" fill={blue} />
        <path d="M207 84l6 14h16l-12 10 4 16-14-8-14 8 4-16-12-10h16z" fill={surface} />
        <rect x="286" y="68" width="86" height="64" rx="12" fill={surface} stroke={red} strokeWidth="2" />
        <circle cx="329" cy="100" r="12" stroke={red} strokeWidth="2" />
        <path d="M324 100h10M329 95v10" stroke={red} strokeWidth="2" strokeLinecap="round" />
        <rect x="408" y="68" width="80" height="64" rx="12" fill={surface} stroke={blue} strokeWidth="2" />
        <path d="M432 102l8 8 16-18" stroke={blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg {...svgProps}>
      <circle cx="150" cy="40" r="56" fill={blue} opacity="0.08" />
      <rect x="48" y="46" width="160" height="112" rx="12" stroke={muted} strokeWidth="2" strokeDasharray="7 6" />
      <path d="M70 78h70M70 98h52M70 118h40" stroke={muted} strokeWidth="2" strokeLinecap="round" />
      <path d="M236 102h40" stroke={red} strokeWidth="2" strokeLinecap="round" />
      <path d="M266 90l14 12-14 12" stroke={red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="306" y="40" width="168" height="124" rx="12" fill={blue} />
      <path d="M326 72h88M326 92h64" stroke={surface} strokeWidth="2" strokeLinecap="round" />
      <path d="M326 122l12 12 28-30" stroke={surface} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const Services = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const band = dark ? '#0066b2' : '#0e2744';
  const colors: MarkColors = {
    blue: dark ? '#3ea6ff' : '#0066b2',
    red: dark ? '#ff3d5c' : '#dc052d',
    muted: dark ? 'rgba(197, 212, 228, 0.55)' : 'rgba(16, 32, 51, 0.35)',
    surface: dark ? '#0e1c2e' : '#e8f1f8',
  };

  return (
    <>
      <PageField>
        <PageContainer>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 220px' },
              gap: { xs: 3, md: 4 },
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                Services
              </Typography>
              <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.25rem' }, maxWidth: 720, mt: 1 }}>
                What we build
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 620, fontSize: '1.125rem', lineHeight: 1.6, mt: 2 }}>
                Custom software development, application modernization, AI in a production workflow, and prototypes taken through to software a team can maintain.
              </Typography>
            </Box>
            <Photo
              src={photos.earth.src}
              alt={photos.earth.alt}
              credit={photos.earth.credit}
              fade="globe"
              sx={{ width: { xs: 180, md: 220 }, height: { xs: 180, md: 220 }, mx: { xs: 'auto', md: 0 }, justifySelf: { md: 'end' } }}
            />
          </Box>
        </PageContainer>
      </PageField>

      <PageContainer>
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {services.map((service, index) => (
            <Box
              key={service.id}
              id={service.id}
              component="li"
              aria-labelledby={`${service.id}-title`}
              sx={{
                scrollMarginTop: '96px',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.15fr) minmax(260px, 0.85fr)' },
                columnGap: { md: 8 },
                rowGap: 3,
                py: { xs: 4.5, md: 6 },
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Box>
                <Box
                  sx={{
                    mb: 3,
                    maxWidth: 520,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: colors.surface,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <ServiceArt index={index} colors={colors} />
                </Box>
                <Typography
                  color="primary"
                  sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontSize: '1.35rem', fontWeight: 600 }}
                >
                  {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography
                  id={`${service.id}-title`}
                  variant="h2"
                  sx={{ fontSize: { xs: '1.85rem', md: '2.35rem' }, mt: 1.5, maxWidth: 520 }}
                >
                  {service.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 2, maxWidth: 520, fontSize: '1.125rem', lineHeight: 1.65 }}>
                  {service.summary}
                </Typography>
              </Box>
              <Box sx={{ alignSelf: { md: 'center' } }}>
                <Typography
                  component="p"
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', m: 0 }}
                >
                  What this covers
                </Typography>
                <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.25 }}>
                  {service.includes.map((item) => (
                    <Typography key={item} component="li" sx={{ mb: 1, lineHeight: 1.5 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
                <Box sx={{ mt: 3, pt: 2.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography
                    color="primary"
                    sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}
                  >
                    What you get
                  </Typography>
                  <Typography sx={{ mt: 1, lineHeight: 1.55 }}>{service.outcome}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </PageContainer>

      <Box sx={{ bgcolor: band, color: '#ffffff', borderTop: dark ? '4px solid #dc052d' : 'none' }}>
        <PageContainer>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.85rem', md: '2.25rem' }, color: 'inherit', maxWidth: 640 }}>
            Not sure which service fits?
          </Typography>
          <Typography sx={{ maxWidth: 560, mt: 1.5, color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}>
            Tell us about the system and the outcome you need. We will say which service fits, or that we are not the right firm.
          </Typography>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            sx={{ mt: 3, bgcolor: '#ffffff', color: '#0e2744', '&:hover': { bgcolor: '#e8eef5' } }}
          >
            Describe the work
          </Button>
        </PageContainer>
      </Box>
    </>
  );
};

export default Services;
